import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar, FileText, MapPin, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function HearingsSummary() {
  const [hearings, setHearings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchHearings();
  }, [period]);

  const fetchHearings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/hearings/summary?period=${period}`);
      setSummary(response.data);
      setHearings(response.data.hearings);
    } catch (error) {
      toast.error('فشل تحميل ملخص الجلسات');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'منظورة': 'bg-blue-100 text-blue-700',
      'مؤجلة': 'bg-yellow-100 text-yellow-700',
      'محجوزة للحكم': 'bg-purple-100 text-purple-700',
      'شطب': 'bg-gray-100 text-gray-700',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-KW', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-KW', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntil = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'اليوم';
    if (diff === 1) return 'غداً';
    if (diff < 0) return 'منتهية';
    return `بعد ${diff} يوم`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">ملخص الجلسات القادمة</h1>
          <p className="text-gray-500 mt-1">جدول بجميع الجلسات القادمة</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button
          data-testid="period-week"
          variant={period === 'week' ? 'default' : 'outline'}
          onClick={() => setPeriod('week')}
          className={period === 'week' ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          أسبوع
        </Button>
        <Button
          data-testid="period-2weeks"
          variant={period === '2weeks' ? 'default' : 'outline'}
          onClick={() => setPeriod('2weeks')}
          className={period === '2weeks' ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          أسبوعين
        </Button>
        <Button
          data-testid="period-month"
          variant={period === 'month' ? 'default' : 'outline'}
          onClick={() => setPeriod('month')}
          className={period === 'month' ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          شهر
        </Button>
        <Button
          data-testid="period-past"
          variant={period === 'past' ? 'default' : 'outline'}
          onClick={() => setPeriod('past')}
          className={period === 'past' ? 'bg-orange-600 hover:bg-orange-700' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}
        >
          الجلسات الفائتة
        </Button>
        <Button
          data-testid="period-all"
          variant={period === 'all' ? 'default' : 'outline'}
          onClick={() => setPeriod('all')}
          className={period === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          كل القضايا
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">إجمالي الجلسات</p>
                  <p className="text-3xl font-bold text-blue-900 mt-2">{summary.total_hearings}</p>
                </div>
                <Calendar className="w-12 h-12 text-blue-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">الجلسات القادمة</p>
                  <p className="text-3xl font-bold text-green-900 mt-2">{summary.future_hearings || 0}</p>
                </div>
                <Calendar className="w-12 h-12 text-green-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">الجلسات الفائتة</p>
                  <p className="text-3xl font-bold text-orange-900 mt-2">{summary.past_hearings || 0}</p>
                </div>
                <Calendar className="w-12 h-12 text-orange-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">الفترة</p>
                  <p className="text-xl font-bold text-purple-900 mt-2">
                    {period === 'week' && 'أسبوع'}
                    {period === '2weeks' && 'أسبوعين'}
                    {period === 'month' && 'شهر'}
                    {period === 'past' && 'الفائتة'}
                    {period === 'all' && 'الكل'}
                  </p>
                </div>
                <FileText className="w-12 h-12 text-purple-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>جدول الجلسات</CardTitle>
        </CardHeader>
        <CardContent>
          {hearings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">لا توجد جلسات في هذه الفترة</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">القضية</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">التاريخ والوقت</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">المحكمة</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">الحالة</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">الملاحظات</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">الإجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {hearings.map((hearing, index) => (
                    <tr 
                      key={index}
                      data-testid={`hearing-row-${index}`}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div>
                          <Link 
                            to={`/cases/${hearing.case_id}`}
                            className="font-semibold text-blue-600 hover:underline"
                          >
                            {hearing.case_number}
                          </Link>
                          <p className="text-sm text-gray-500 mt-1">
                            {hearing.auto_number}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatShortDate(hearing.hearing_date)}
                          </p>
                          <Badge 
                            variant="outline" 
                            className={`mt-1 text-xs ${
                              hearing.is_past 
                                ? 'bg-orange-50 border-orange-300 text-orange-700' 
                                : 'bg-green-50 border-green-300 text-green-700'
                            }`}
                          >
                            {hearing.is_past ? 'فائتة' : getDaysUntil(hearing.hearing_date)}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-gray-900">{hearing.court || '-'}</p>
                            {hearing.department && (
                              <p className="text-xs text-gray-500 mt-0.5">{hearing.department}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={getStatusBadge(hearing.status)}>
                          {hearing.status || 'غير محدد'}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-sm text-gray-600 max-w-xs">
                          {hearing.hearing_notes || '-'}
                        </p>
                      </td>
                      <td className="py-4 px-4">
                        <Link to={`/cases/${hearing.case_id}`}>
                          <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                            عرض التفاصيل
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}